import { useAsyncData, useRuntimeConfig } from '#imports'
import { type Ref, computed } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'

import { MAX_REVIEWS } from '~/scopes/product-core/components/containers/ContainerProductDetailPageGallery/composables/useFetchAutomatedReviewsForCarousel/useFetchAutomatedReviewsForCarousel.constants'
import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import {
  YOUTUBE_VIDEO_THUMBNAIL_URL,
  YOUTUBE_VIDEO_TITLE,
} from '~/scopes/product-core/components/presentationals/GalleryNew/components/YoutubeVideo/YoutubeVideo.constants'
import type {
  ProductDetailPageGalleryImage,
  ProductDetailPageGalleryItem,
  ProductDetailPageGalleryVideo,
} from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.types'

function mapBaseImages(
  images: GetProductResponse['images'],
  allowPartnersImages: boolean,
): ProductDetailPageGalleryImage[] {
  return images
    .filter((image) => allowPartnersImages || image.source !== 'PARTNER')
    .map((image, index) => ({
      id: `base-${index}-${image.url}`,
      src: image.url,
      alt: image.description,
    }))
}

function toReviewItem(review: ReviewWithCount): ProductDetailPageGalleryItem {
  return {
    kind: 'review',
    review,
    // Default size, will be adjusted by layout logic
    size: 'small',
  }
}

function toVideoItem(
  video: ProductDetailPageGalleryVideo,
): ProductDetailPageGalleryItem {
  return {
    kind: 'video',
    video,
    // Default size
    size: 'small',
  }
}

export function useFetchGalleryData({
  productId,
  images,
  allowPartnersImages,
  reviewsFromProduct,
  areReviewsPending,
}: {
  productId: Ref<string>
  images: Ref<GetProductResponse['images']>
  allowPartnersImages: Ref<boolean>
  reviewsFromProduct: Ref<ReviewWithCount[] | undefined>
  areReviewsPending: Ref<boolean>
}) {
  const config = useRuntimeConfig()

  const key = computed(() => `gallery-data-${productId.value}`)

  return useAsyncData(
    key.value,
    async () => {
      const baseImages = mapBaseImages(images.value, allowPartnersImages.value)

      const isVideoEnabled =
        config.public.FF_KILL_VIDEOS_IN_NEW_CAROUSEL === false

      const injectableItems: ProductDetailPageGalleryItem[] = []

      // Add Reviews (or skeleton placeholders while loading)
      if (reviewsFromProduct.value) {
        injectableItems.push(...reviewsFromProduct.value.map(toReviewItem))
      } else if (areReviewsPending.value) {
        for (let i = 0; i < MAX_REVIEWS; i += 1) {
          injectableItems.push({ kind: 'skeleton', size: 'small' })
        }
      }

      // Add Video
      if (isVideoEnabled) {
        injectableItems.push(
          toVideoItem({
            id: `extra:video-${YOUTUBE_VIDEO_THUMBNAIL_URL}`,
            src: YOUTUBE_VIDEO_THUMBNAIL_URL,
            alt: YOUTUBE_VIDEO_TITLE,
          }),
        )
      }

      return {
        baseImages,
        injectableItems,
      }
    },
    {
      watch: [
        productId,
        images,
        allowPartnersImages,
        reviewsFromProduct,
        areReviewsPending,
      ],
    },
  )
}
